/**
 * 查询钩子（TanStack Query）。键名按「资源 + 参数」，默认值照参照实现 console/fulfillment/app：
 * 只重试可重试的错误、最多 2 次、指数退避；窗口切回来不自动重拉，断网恢复时重拉。
 */
import {
  keepPreviousData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiGet, apiPost, isApiError, isRetryableRead } from './client';
import { API } from './endpoints';
import type {
  AccessLogQuery,
  AccessLogResponse,
  CallingContextDetail,
  ConversationHistoryResponse,
  ConversationFilter,
  ConversationSearchResponse,
  ConversationPage,
  DeviceHistory,
  DeviceListQuery,
  DeviceListResponse,
  DeviceStatusResponse,
  DeviceUsageInfo,
  DiaryListResponse,
  FaceRecord,
  GrantRequest,
  GrantResponse,
  Me,
  MemoryData,
  Paged,
  TestDevice,
  TestTurn,
  VoiceprintRecord,
} from './types';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => failureCount < 2 && isRetryableRead(error),
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        staleTime: 15_000,
      },
      mutations: { retry: false },
    },
  });
}

export const keys = {
  me: ['me'] as const,
  devices: (q: DeviceListQuery) => ['devices', q] as const,
  device: (sn: string, part: string, ...args: unknown[]) => ['device', sn, part, ...args] as const,
  testDevices: (page: number, search: string) => ['test-devices', page, search] as const,
  testConversations: (sn: string, q: string) => ['test-device', sn, 'conversations', q] as const,
  accessLog: (q: AccessLogQuery) => ['access-log', q] as const,
};

// ---- 当前用户 ----

export function useMeQuery() {
  return useQuery({
    queryKey: keys.me,
    queryFn: ({ signal }) => apiGet<Me>(API.me, undefined, signal),
    // 没登录（401）不重试（默认的重试只管服务器 / 网络这一类）：直接去入口换通行证
    staleTime: 60_000,
  });
}

// ---- 设备列表 ----

function deviceListQuery(q: DeviceListQuery) {
  return {
    page: q.page,
    pageSize: q.pageSize,
    sortBy: q.sortBy,
    sortDir: q.sortDir,
    search: q.search,
    status: q.status && q.status !== 'all' ? q.status : undefined,
    cohort: q.cohort && q.cohort !== 'all' ? q.cohort : undefined,
  };
}

export function useDeviceList(q: DeviceListQuery, enabled = true) {
  return useQuery({
    queryKey: keys.devices(q),
    queryFn: ({ signal }) => apiGet<DeviceListResponse>(API.devices, deviceListQuery(q), signal),
    // 换搜索词、排序、页码时保留上一页：不然表格先变回骨架，搜索框也跟着被卸载
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** 设备列表里这台的那一行（机主、活跃天数、近两天轮次），给详情的「状态」页签 */
export function useDeviceRow(sn: string) {
  const q: DeviceListQuery = { page: 1, pageSize: 1, sortBy: 'lastActive', sortDir: 'desc', search: sn };
  return useQuery({
    queryKey: keys.devices(q),
    queryFn: ({ signal }) => apiGet<DeviceListResponse>(API.devices, deviceListQuery(q), signal),
    select: (r) => r.items.find((d) => d.sn === sn) ?? null,
  });
}

// ---- 设备详情 ----

export function useDeviceStatus(sn: string) {
  return useQuery({
    queryKey: keys.device(sn, 'status'),
    queryFn: ({ signal }) => apiGet<DeviceStatusResponse>(API.deviceStatus(sn), undefined, signal),
    // 读数每 15 秒刷新一次
    refetchInterval: 15_000,
    staleTime: 5_000,
  });
}

export function useDeviceHistory(sn: string) {
  return useQuery({
    queryKey: keys.device(sn, 'history'),
    queryFn: ({ signal }) => apiGet<DeviceHistory>(API.deviceHistory(sn), undefined, signal),
    staleTime: 30_000,
  });
}

export function useDeviceFaces(sn: string) {
  return useQuery({
    queryKey: keys.device(sn, 'faces'),
    queryFn: ({ signal }) => apiGet<FaceRecord[]>(API.deviceFaces(sn), undefined, signal),
    staleTime: 30_000,
  });
}

export function useDeviceVoiceprints(sn: string) {
  return useQuery({
    queryKey: keys.device(sn, 'voiceprints'),
    queryFn: ({ signal }) => apiGet<VoiceprintRecord[]>(API.deviceVoiceprints(sn), undefined, signal),
    staleTime: 30_000,
  });
}

export function useDeviceMemory(sn: string, enabled = true) {
  return useQuery({
    queryKey: keys.device(sn, 'memory'),
    queryFn: ({ signal }) => apiGet<MemoryData>(API.deviceMemory(sn), undefined, signal),
    staleTime: 30_000,
    enabled,
  });
}

// 一次 24 篇：卡片一行 2～8 张（随宽度），12 篇在 2560 宽下是 7 + 5 的参差；线上每台中位 44 篇（汤祺 10-06）
const DIARY_PAGE = 24;

/** 日记：「加载更早的」一页一页往后取 */
export function useDiaries(sn: string) {
  return useInfiniteQuery({
    queryKey: keys.device(sn, 'diaries'),
    queryFn: ({ pageParam, signal }) =>
      apiGet<DiaryListResponse>(API.deviceDiaries(sn), { page: pageParam, pageSize: DIARY_PAGE }, signal),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    staleTime: 30_000,
  });
}

// ---- 对话 ----

export const CONVERSATION_PAGE_SIZE = 20;

function conversationPageQuery(sn: string, page: number, live: boolean, filter: ConversationFilter, client: QueryClient, view: number) {
  return {
    queryKey: keys.device(sn, 'conversations', page, filter, view),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const data = await apiGet<ConversationHistoryResponse>(API.conversationHistory(sn), { ...filter, page, pageSize: CONVERSATION_PAGE_SIZE }, signal);
      // 第一页已在服务端固定记录上限。绑定这个上限时复用同一份结果，避免再发一次内容请求与列表闪空。
      if (!live && !filter.snapshotId && data.snapshotId !== undefined) {
        client.setQueryData(keys.device(sn, 'conversations', page, { ...filter, asOf: data.asOf, snapshotId: data.snapshotId, filteredSnapshotId: data.filteredSnapshotId }, view), data);
      }
      return data;
    },
    // 已经载入的旧页不会变；最新一页 15 秒内不重取，「自动刷新」打开时每秒重取
    staleTime: !filter.date && page === 1 ? 15_000 : Infinity,
    refetchOnReconnect: !filter.date,
    refetchInterval: live && page === 1 ? 1000 : (false as const),
  };
}

/** 对话记录的连续几页（第 1 页最新）。时间流把它们拼起来 */
export function useConversationPages(sn: string, pages: number[], live: boolean, filter: ConversationFilter = {}, view = 0) {
  const client = useQueryClient();
  return useQueries({ queries: pages.map((p) => conversationPageQuery(sn, p, live, filter, client, view)) });
}

export function useDeviceUsageInfo(sn: string) {
  return useQuery({
    queryKey: keys.device(sn, 'usage-info'),
    queryFn: ({ signal }) => apiGet<DeviceUsageInfo>(API.deviceUsageInfo(sn), undefined, signal),
    staleTime: 30_000,
  });
}

/**
 * 这台设备一共多少轮对话（「对话」页签上的数字）。单独一个接口，不从对话记录里顺手拿：
 * 打开设备详情就要显示这个数，取对话记录等于每个打开设备的人都被记一笔「看了对话」，
 * 要选理由的角色还会被要求先选理由（console/docs/specs/问题诊断查看权限.md）。
 */
export function useConversationTotal(sn: string) {
  return useQuery({
    queryKey: keys.device(sn, 'conversation-count'),
    queryFn: ({ signal }) => apiGet<{ total: number }>(API.conversationCount(sn), undefined, signal),
    select: (r) => r.total,
    staleTime: 60_000,
  });
}

export function useConversationSearch(sn: string, q: string, filter: ConversationFilter = {}) {
  return useInfiniteQuery({
    queryKey: keys.device(sn, 'search', q, filter),
    initialPageParam: { page: 1, asOf: filter.asOf, snapshotId: filter.snapshotId, filteredSnapshotId: filter.filteredSnapshotId },
    queryFn: ({ signal, pageParam }) => apiGet<ConversationSearchResponse>(API.conversationSearch(sn), { ...filter, ...pageParam, q, pageSize: CONVERSATION_PAGE_SIZE }, signal),
    getNextPageParam: (last) => last.page * last.pageSize < last.total
      ? { page: last.page + 1, asOf: last.asOf, snapshotId: last.snapshotId, filteredSnapshotId: last.filteredSnapshotId } : undefined,
    enabled: q.length > 0,
    staleTime: filter.date ? Infinity : 30_000,
    refetchOnReconnect: !filter.date,
  });
}

/** 搜索结果里的一轮在对话记录的第几页（「在对话中定位」） */
export function fetchConversationPage(sn: string, id: string, filter: ConversationFilter = {}, signal?: AbortSignal) {
  return apiGet<ConversationPage>(API.conversationPage(sn), { ...filter, id }, signal);
}

/** 按对话表的 trace_id 找这一轮在第几页（地址里的 ?trace=，产品分析「语言错误」跳过来带的） */
export function fetchConversationPageByTrace(sn: string, trace: string, filter: ConversationFilter = {}, signal?: AbortSignal) {
  return apiGet<ConversationPage>(API.conversationPage(sn), { ...filter, trace }, signal);
}

export function useConversationContext(sn: string, traceId: string) {
  return useQuery({
    queryKey: keys.device(sn, 'context', traceId),
    queryFn: ({ signal }) =>
      apiGet<CallingContextDetail | Record<string, never>>(API.conversationContext(sn), { turn_id: traceId }, signal),
    staleTime: Infinity,
    // 查不到时 sidecar 回空对象：归成 null，界面写「查不到」
    select: (d) => (Object.keys(d).length ? (d as CallingContextDetail) : null),
  });
}

// ---- 查看权限 ----

/**
 * 登记一台设备当天的查看理由。成功后把因为「要先选理由」而失败的查询全部重取一遍——
 * 同一台设备的几块内容（对话、上下文、搜索……）可能同时停在理由面板上，登记一次全部放行。
 * 也把查看记录标成过期：刚登记的这一条要出现在「查看记录」里。
 */
export function useGrantMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GrantRequest) => apiPost<GrantResponse>(API.accessGrants, body),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['access-log'] }),
        qc.invalidateQueries({
          predicate: (q) => isApiError(q.state.error) && q.state.error.code === 'reason_required',
        }),
      ]);
    },
  });
}

export const ACCESS_LOG_PAGE_SIZE = 50;

export function useAccessLog(q: AccessLogQuery) {
  return useQuery({
    queryKey: keys.accessLog(q),
    queryFn: ({ signal }) =>
      apiGet<AccessLogResponse>(API.accessLog, { from: q.from, to: q.to, user: q.user, device: q.device, page: q.page, pageSize: q.pageSize }, signal),
    placeholderData: keepPreviousData,
  });
}

// ---- 测网设备 ----

export const TEST_LIST_PAGE_SIZE = 50;
export const TEST_TURN_PAGE_SIZE = 20;

export function useTestDevices(page: number, search: string) {
  return useQuery({
    queryKey: keys.testDevices(page, search),
    queryFn: ({ signal }) =>
      apiGet<Paged<TestDevice>>(API.testDevices, { page, pageSize: TEST_LIST_PAGE_SIZE, search }, signal),
    placeholderData: keepPreviousData,
  });
}

export function useTestConversations(sn: string, q: string) {
  return useInfiniteQuery({
    queryKey: keys.testConversations(sn, q),
    queryFn: ({ pageParam, signal }) =>
      apiGet<Paged<TestTurn>>(API.testConversations(sn), { page: pageParam, pageSize: TEST_TURN_PAGE_SIZE, q }, signal),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.pageSize < last.total ? last.page + 1 : undefined),
  });
}
